import { ClawbertMark } from "../ClawbertMark";

interface WorldFallbackProps {
  /** Optional feature labels pinned to the drawn objects. */
  labels?: { computer: string; fileCabinet: string; mailbox: string };
}

/**
 * Light 2D stand-in for the 3D hero world on small screens and reduced
 * motion. Same cast, no WebGL.
 */
export function WorldFallback({ labels }: WorldFallbackProps) {
  return (
    <div className="relative h-full w-full overflow-hidden bg-gradient-to-b from-[#6FA0B8] to-[#4A7A93]">
      {/* Caustic shimmer */}
      <div className="pointer-events-none absolute inset-0 opacity-25">
        <svg viewBox="0 0 400 300" className="h-full w-full" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
          <g fill="none" stroke="#DCEfF5" strokeWidth="1.5" opacity="0.6">
            <path d="M-20 60 Q60 30 140 60 T300 60 T460 60" />
            <path d="M-20 130 Q60 100 140 130 T300 130 T460 130" />
            <path d="M-20 200 Q60 170 140 200 T300 200 T460 200" />
          </g>
        </svg>
      </div>

      {/* Bubbles */}
      <div className="pointer-events-none absolute top-6 left-8 h-3 w-3 rounded-full bg-white/25" />
      <div className="pointer-events-none absolute top-16 right-12 h-2 w-2 rounded-full bg-white/20" />
      <div className="pointer-events-none absolute top-10 right-24 h-4 w-4 rounded-full bg-white/15" />

      {/* Sand island */}
      <svg viewBox="0 0 400 300" className="absolute inset-0 h-full w-full" aria-hidden="true" preserveAspectRatio="xMidYMax slice">
        <ellipse cx="200" cy="285" rx="215" ry="70" fill="#D4BC82" />
        <ellipse cx="200" cy="278" rx="200" ry="62" fill="#E8D5A3" />

        {/* Corals */}
        <g transform="translate(48 218)">
          <rect x="-4" y="-34" width="8" height="36" rx="4" fill="#F28B82" />
          <rect x="-16" y="-24" width="7" height="26" rx="3.5" fill="#F28B82" transform="rotate(-18)" />
          <rect x="10" y="-26" width="7" height="28" rx="3.5" fill="#F7CE68" transform="rotate(14)" />
        </g>
        <g transform="translate(352 226)">
          <circle cx="0" cy="-10" r="13" fill="#F48FB1" />
          <circle cx="-13" cy="-2" r="9" fill="#F48FB1" />
          <circle cx="12" cy="-3" r="8" fill="#6ECFCF" />
        </g>

        {/* Mailbox */}
        <g transform="translate(310 200)">
          <rect x="-3" y="0" width="6" height="34" fill="#D4BC82" stroke="#B89B60" strokeWidth="1" />
          <rect x="-20" y="-24" width="40" height="26" rx="8" fill="#B39DDB" />
          <rect x="-14" y="-16" width="22" height="3" rx="1.5" fill="#3A3450" />
          <rect x="20" y="-32" width="4" height="14" rx="2" fill="#F28B82" />
          <rect x="20" y="-34" width="10" height="6" rx="2" fill="#F28B82" />
        </g>

        {/* Retro computer */}
        <g transform="translate(120 232)">
          <rect x="-26" y="-36" width="52" height="40" rx="6" fill="#FFF8E1" />
          <rect x="-19" y="-29" width="38" height="24" rx="3" fill="#2D4A3E" />
          <rect x="-15" y="-24" width="18" height="3" fill="#A8E6CF" />
          <rect x="-15" y="-18" width="26" height="3" fill="#A8E6CF" opacity="0.7" />
          <rect x="-15" y="-12" width="12" height="3" fill="#A8E6CF" opacity="0.5" />
        </g>

        {/* File cabinet */}
        <g transform="translate(240 214)">
          <rect x="-16" y="-44" width="32" height="48" rx="4" fill="#6ECFCF" />
          <rect x="-11" y="-38" width="22" height="10" rx="2" fill="#4AA8A8" />
          <rect x="-11" y="-24" width="22" height="10" rx="2" fill="#4AA8A8" />
          <rect x="-11" y="-10" width="22" height="10" rx="2" fill="#4AA8A8" />
        </g>
      </svg>

      {/* Clawbert front and center */}
      <div className="absolute bottom-2 left-1/2 w-36 -translate-x-1/2">
        <ClawbertMark pose="wave" className="h-auto w-full drop-shadow-lg" />
      </div>

      {/* Static feature labels */}
      {labels && (
        <>
          <span className="absolute bottom-[38%] left-[14%] rounded-full bg-home-ink/80 px-2.5 py-1 font-body text-[11px] font-bold text-home-cream">
            {labels.computer}
          </span>
          <span className="absolute bottom-[44%] left-1/2 -translate-x-1/2 rounded-full bg-home-ink/80 px-2.5 py-1 font-body text-[11px] font-bold text-home-cream">
            {labels.fileCabinet}
          </span>
          <span className="absolute right-[6%] bottom-[42%] rounded-full bg-home-ink/80 px-2.5 py-1 font-body text-[11px] font-bold text-home-cream">
            {labels.mailbox}
          </span>
        </>
      )}
    </div>
  );
}
